<script setup>
import useVuelidate from "@vuelidate/core"
import {email, helpers, maxLength, minLength, numeric, required} from "@vuelidate/validators"
import {reactive, watch} from "vue"

import InputCheckbox from "@shared/components/atoms/form/InputCheckbox.vue"
import InputRadio from "@shared/components/atoms/form/InputRadio.vue"
import InputText from "@shared/components/atoms/form/InputText.vue"
import useSharedRouter from "@shared/router"
import {useAccountStore} from "@shared/stores/AccountStore"
import {useAffiliateStore} from "@shared/stores/AffiliateStore"
import {useAuthStore} from "@shared/stores/AuthStore"
import {useGamePlayStore} from "@shared/stores/GamePlayStore"
import {useNotificationsStore} from "@shared/stores/NotificationsStore"
import {useTermsAndConditionsStore} from "@shared/stores/TermsAndConditionsStore"

const accountStore = useAccountStore()
const affiliateStore = useAffiliateStore()
const authStore = useAuthStore()
const gamePlayStore = useGamePlayStore()
const notificationsStore = useNotificationsStore()
const termsAndConditionsStore = useTermsAndConditionsStore()
const router = useSharedRouter()

const state = reactive({
    failedAttempts: 0,
    isSubmitting: false,
    form: {
        login: {
            email: '',
            password: '',
        },
        forgotPassword: {
            email: '',
        },
        registration: {
            name: '',
            email: '',
            mobileNumber: '',
            password: '',
            passwordConfirm: '',
            confirm18: false,
            gender: '',
            dobDay: '',
            dobMonth: '',
            dobYear: '',
        }
    }
})

const loginForm = useVuelidate({
    email: {required},
    password: {required},
}, state.form.login, {$stopPropagation: true})

const forgotPasswordForm = useVuelidate({
    email: {required, email},
}, state.form.forgotPassword, {$stopPropagation: true})

const registrationForm = useVuelidate({
    name: {
        required
    },
    email: {
        required,
        email
    },
    mobileNumber: {
        required,
        numeric,
        minLength: helpers.withMessage('Please enter a valid mobile number.', minLength(9)),
        maxLength: helpers.withMessage('Please enter a valid mobile number.', maxLength(10))
    },
    password: {
        required,
    },
    passwordConfirm: {
        required,
        sameAs: helpers.withMessage(
            'Passwords do not match.',
            (value) => value === state.form.registration.password
        ),
    },
    gender: {
        required,
    },
    confirm18: {
        sameAs: helpers.withMessage(
            'You must agree with terms and conditions.',
            (value) => value === true
        ),
    },
    dobDay: {
        required,
        numeric
    },
    dobMonth: {
        required,
        numeric
    },
    dobYear: {
        required,
        numeric
    }
}, state.form.registration, {$stopPropagation: true})

const setAuthModalMode = (mode) => {
    loginForm.value.$reset()
    forgotPasswordForm.value.$reset()
    registrationForm.value.$reset()

    authStore.setAuthModalMode(mode)
}

const handleLogin = async () => {
    if (state.isSubmitting) {
        return
    }

    loginForm.value.$touch()
    if (!loginForm.value.$invalid) {
        state.isSubmitting = true

        try {
            await authStore.postLogin(state.form.login)

            accountStore.fetchUser()
            authStore.setAuthModalVisibility(false)
            router.push({name: 'home'})
        } catch (error) {
            state.failedAttempts++
            notificationsStore.showAlert(error)
            if (state.failedAttempts >= 3) {
                setAuthModalMode('forgotPassword')
                state.failedAttempts = 0
            }
        } finally {
            state.isSubmitting = false
        }
    }
}

const handleForgotPassword = async () => {
    forgotPasswordForm.value.$touch()
    if (!forgotPasswordForm.value.$invalid) {
        let message = 'Email not sent, please try again. If the issue persist, contact support.'
        try {
            const {data} = await authStore.postPasswordReset(forgotPasswordForm.value.email.$model)
            if (data.success) {
                authStore.setAuthModalVisibility(false)
                message = 'Password reset link has been sent to your email.'
            }
        } catch (error) {
            //
        }

        notificationsStore.showAlert(message)
    }
}

const handleRegistration = () => {
    if (state.isSubmitting) {
        return
    }

    registrationForm.value.$touch()
    if (!registrationForm.value.$invalid) {
        state.isSubmitting = true

        const registrationForm = state.form.registration
        authStore.postRegistration({
            name: registrationForm.name,
            email: registrationForm.email,
            mobile_number: registrationForm.mobileNumber,
            password: registrationForm.password,
            password_confirmation: registrationForm.passwordConfirm,
            dob: `${registrationForm.dobYear}/${registrationForm.dobMonth}/${registrationForm.dobDay}`,
            gender: registrationForm.gender,
            confirm18: registrationForm.confirm18,
            affiliate: affiliateStore.affiliate,
            tracking_id: affiliateStore.trackingId,
        }).then(() => {
            accountStore.fetchUser(true)
            authStore.setAuthModalVisibility(false)
            router.push({name: 'home'})
        })
        .catch((error) => {
            notificationsStore.showAlert(error)
        })
        .finally(() => {
            state.isSubmitting = false
        })
    }
}

watch(
    () => accountStore.isAuthenticated,
    () => {
        if (gamePlayStore.playGameAttempt) {
            const { gameId, isDemo } = gamePlayStore.playGameAttempt
            gamePlayStore.playGame(gameId, isDemo)
        }
    }
)

</script>

<template>
    <div
        v-if="authStore.isAuthModalVisible"
        id="divAppLogin"
        :class="{
            'show': true,
            [authStore.currentAuthModalMode]: true
         }"
    >
        <div class="divLoginOverlay"></div>

        <div class="divLoginPopup">
            <div id="btnCloseLogin" @click="authStore.setAuthModalVisibility(false)"></div>
            <div
                v-if="authStore.currentAuthModalMode === 'login'"
                class="divLoginForm"
            >
                <div class="divLoginHeading">Login to {{ appName }}</div>

                <div id="txtLoginUsername">
                    <InputText
                        label="Username/Email"
                        name="txtLoginUsername"
                        type="email"
                        v-model="loginForm.email.$model"
                        :validator="loginForm.email"
                        @keyup.enter="handleLogin"
                    />
                </div>
                <div id="txtLoginPassword">
                    <InputText
                        label="Password"
                        type="password"
                        name="txtLoginPassword"
                        v-model="loginForm.password.$model"
                        :validator="loginForm.password"
                        @keyup.enter="handleLogin"
                    />
                </div>
                <div id="divForgotPassword" @click="() => setAuthModalMode('forgotPassword')">Forgot password?</div>
                <button id="btnSignIn" class="btnGreen" @click="handleLogin">LOG IN</button>
                <div class="divDontHaveAccount">Don't have an account?</div>
                <div id="divRegisterLink" @click="() => setAuthModalMode('registration')">Register</div>
            </div>

            <div
                v-if="authStore.currentAuthModalMode === 'forgotPassword'"
                class="divForgotPasswordForm"
            >
                <div class="divLoginHeading">Reset password</div>
                <div id="txtForgotPasswordEmail">
                    <InputText
                        label="Email"
                        type="email"
                        v-model="forgotPasswordForm.email.$model"
                        :validator="forgotPasswordForm.email"
                        @keyup.enter="handleForgotPassword"
                    />
                </div>
                <button id="btnReset" class="btnGreen" @click="handleForgotPassword">RESET</button>
                <div class="divBackTo">Back to</div>
                <div id="divSignInLink1" @click="() => setAuthModalMode('login')">Sign In</div>
            </div>

            <div
                v-if="authStore.currentAuthModalMode === 'registration'"
                class="divRegisterForm"
            >
                <div class="divLoginHeading">Welcome to {{ appName }}</div>

                <div id="txtRegisterUsername">
                    <InputText
                        label="Username"
                        autocomplete="off"
                        v-model="registrationForm.name.$model"
                        :validator="registrationForm.name"
                    />
                </div>
                <div id="txtRegisterEmail">
                    <InputText
                        label="Email"
                        autocomplete="off"
                        type="email"
                        v-model="registrationForm.email.$model"
                        :validator="registrationForm.email"
                    />
                </div>
                <div id="txtRegisterMobileNumber">
                    <InputText
                        label="Mobile number"
                        type="number"
                        v-model="registrationForm.mobileNumber.$model"
                        :validator="registrationForm.mobileNumber"
                    />
                </div>
                <div id="txtRegisterPassword">
                    <InputText
                        label="Password"
                        type="password"
                        v-model="registrationForm.password.$model"
                        :validator="registrationForm.password"
                    />
                </div>

                <div id="txtRegisterConfirmPassword">
                    <InputText
                        label="Confirm password"
                        type="password"
                        v-model="registrationForm.passwordConfirm.$model"
                        :validator="registrationForm.passwordConfirm"
                    />
                </div>
                <div class="divDateOfBirth">Date of birth</div>
                <div id="txtRegisterDay">
                    <InputText
                        label="Day"
                        type="number"
                        v-model="registrationForm.dobDay.$model"
                        :validator="registrationForm.dobDay"
                    />
                </div>
                <div id="txtRegisterMonth">
                    <InputText
                        label="Month"
                        type="number"
                        v-model="registrationForm.dobMonth.$model"
                        :validator="registrationForm.dobMonth"
                    />
                </div>
                <div id="txtRegisterYear">
                    <InputText
                        label="Year"
                        type="number"
                        v-model="registrationForm.dobYear.$model"
                        :validator="registrationForm.dobYear"
                    />
                </div>
                <div class="divRegisterGenderTypes">
                    <InputRadio
                        v-model="registrationForm.gender.$model"
                        :validator="registrationForm.gender"
                        :options="{
                            'male': 'Male',
                            'female': 'Female',
                        }"
                    />
                </div>

                <InputCheckbox
                    v-model="registrationForm.confirm18.$model"
                    :validator="registrationForm.confirm18"
                >
                    I confirm that I am at least 18 years of age, and accept the
                    <span
                        id="spnTermsAndConditions"
                        @click.stop="termsAndConditionsStore.setTermsAndConditionsModalVisibility(true)"
                    >
                        Terms & Conditions
                    </span>
                </InputCheckbox>

                <button id="btnRegister" class="btnGreen" @click="handleRegistration" style="margin: 10px 0;">REGISTER
                </button>

                <div class="divAlreadyHaveAccount">Already have an account?</div>
                <div id="divSignInLink2" @click="() => setAuthModalMode('login')">Sign In</div>
            </div>
        </div>
    </div>
</template>
